// src/pages/ApplyMembership.js
// ✅ PAYMENT FIXED + ✅ APPLY PAGE DOCUMENT UPLOAD REMOVED (upload later in Member Portal)
// ✅ FINAL FLOW (STRICT, STEP-BY-STEP):
//    Step-1 Complete → Step-2 Unlock → Step-3 Unlock
//    Step-3: Final Submit → (OTP UI opens + OTP send) → Verify OTP → (Account UI opens) → Create Account → Final Submit again → Pay & Auto Approve
// ✅ OTP/Account are hidden by default on Step-3 (only open when needed)
// ✅ FIX: Step pills clipping (overflow-visible) + sticky bar safe
// ✅ Govt-style validations (Aadhaar/PAN/Account/IFSC + Khata/Survey)
// ✅ UI: Govt + Professional (clean, compact, less scroll)

import { useEffect, useMemo, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import { auth } from "../firebaseConfig";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  createUserWithEmailAndPassword,
} from "firebase/auth";

/* =============================
   Razorpay Settings
============================= */
const CREATE_ORDER_ENDPOINT =
  "https://us-central1-shineagrimscs.cloudfunctions.net/createRazorpayOrder";

const MEMBERSHIP_VERIFY_ENDPOINT =
  "https://us-central1-shineagrimscs.cloudfunctions.net/membershipPaymentSuccess";

const RAZORPAY_KEY_ID = "rzp_live_RibYoaGi9Jm8CU";
const FIXED_MEMBERSHIP_FEE = 1000;

const cx = (...xs) => xs.filter(Boolean).join(" ");
const normalizeKey = (s) => String(s || "").trim();

/* =============================
   UI Helpers (MUST be outside to avoid focus loss)
============================= */
function StepBtn({ activeStep, n, title, onClick, locked }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={locked}
      className={cx(
        "px-3.5 py-1.5 rounded-full text-[12px] font-extrabold border transition",
        locked ? "opacity-55 cursor-not-allowed" : "",
        activeStep === n
          ? "bg-slate-900 text-white border-slate-900 shadow-sm"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      )}
    >
      <span className="opacity-80">{n}.</span> {title}
    </button>
  );
}

function SectionCard({ title, sub, children, rightTag = "Government Form" }) {
  return (
    <section className="border border-slate-200 rounded-2xl p-5 bg-white shadow-[0_14px_40px_rgba(15,23,42,0.08)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-extrabold text-slate-900 text-[14px]">{title}</h2>
          {sub ? <div className="text-[12px] text-slate-600 mt-1">{sub}</div> : null}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          {rightTag}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function FieldHint({ children }) {
  return <div className="text-[11px] text-slate-500 mt-1">{children}</div>;
}

function SoftCard({ title, icon, children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-white",
    info: "border-sky-200 bg-sky-50/40",
    warn: "border-amber-200 bg-amber-50",
    ok: "border-emerald-200 bg-emerald-50/50",
    danger: "border-rose-200 bg-rose-50/60",
  };
  return (
    <div className={cx("rounded-2xl border p-4", tones[tone] || tones.slate)}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[12px] font-extrabold text-slate-900">
          {icon ? <span className="mr-1">{icon}</span> : null}
          {title}
        </div>
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Badge({ children, tone = "slate" }) {
  const t = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    info: "bg-sky-50 text-sky-800 border-sky-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-extrabold border",
        t[tone]
      )}
    >
      {children}
    </span>
  );
}

/* =============================
   Verify + Approve (Cloud Function)
============================= */
const verifyAndApprove = async ({ memberDocId, rp }) => {
  const res = await fetch(MEMBERSHIP_VERIFY_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      memberDocId,
      razorpay_order_id: rp?.razorpay_order_id,
      razorpay_payment_id: rp?.razorpay_payment_id,
      razorpay_signature: rp?.razorpay_signature,
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.message || data?.error || "Payment verification failed.");
  }
  return String(data?.member_id || "");
};

/* =========================
   Missing keys auto-detect (Non breaking)
========================= */
const pushMissingApplyKey = (lang, key) => {
  try {
    const storeKey = "__missing_apply_i18n_keys__";
    const existing = JSON.parse(localStorage.getItem(storeKey) || "[]");
    const item = `${lang}::${key}`;
    if (!existing.includes(item)) {
      localStorage.setItem(storeKey, JSON.stringify([...existing, item]));
      // eslint-disable-next-line no-console
      console.warn("[ApplyMembership missing i18n]", item);
    }
  } catch {
    // ignore
  }
};

/* =============================
   Language strings (EN/HI/GU) (kept compact)
============================= */
const I18N = {
  en: {
    society: "Shine Agri Allied & Marketing Multistate Cooperative Society Ltd.",
    title: "Farmer Membership Registration",
    subtitle:
      "Step-wise process: Fill form → Final Submit sends OTP → Verify OTP → Create Password → Pay ₹1000 (Auto Approval).",
    alreadyApplied: "Already Applied?",
    payLaterTitle: "Pay & Auto-Approve using Mobile Number",
    payLaterSub: "Enter Mobile → Fetch Details → Pay ₹1000 → Auto approve",
    enterMobile: "Enter Mobile Number",
    fetchDetails: "Fetch Details",
    checking: "Checking...",
    name: "Name",
    status: "Status",
    fee: "Fee",
    payApprove: "Pay ₹1000 & Approve",
    processing: "Processing...",

    chooseType: "Choose Membership Type",
    regularTag: "Regular Member",
    nominalTag: "Nominal Member",
    regularTitle: "Regular Member (with Shares)",
    nominalTitle: "Nominal Member (without Shares)",
    applyRegular: "Apply as Regular Member",
    applyNominal: "Apply as Nominal Member",
    changeType: "Change Type",
    fixed: "Fixed",

    step1: "Personal & Location",
    step2: "Farmer & KYC",
    step3: "Bank, Nominee & Final",

    s1Title: "1) Personal Details & Location",
    fullName: "Full Name",
    fatherName: "Father's Name",
    dob: "DOB",
    gender: "Gender",
    mobile: "Mobile",
    email: "Gmail ID",
    address: "Address",
    state: "State",
    district: "District",
    taluka: "Taluka",
    village: "Village",
    occupation: "Occupation",
    next: "Next",
    back: "Back",

    otpTitle: "Mobile OTP Verification",
    otpSub: "OTP will be asked only at Final Submit before payment.",
    sendOtp: "Send OTP",
    resendOtp: "Resend OTP",
    otpPH: "Enter 6-digit OTP",
    verifyOtp: "Verify",
    verified: "Verified ✅",

    accountTitle: "Create Login Account",
    password: "Create Password",
    confirmPassword: "Confirm Password",
    create: "Create Account",
    created: "Account Created ✅",
    accountNote:
      "Gmail will receive password reset link. Account is mandatory before payment.",

    s2Title: "2) Farmer Details, KYC & Crop Selection",
    farmerCategory: "Farmer Category",
    landArea: "Land Area (Hectare)",
    khataNo: "Khata No",
    surveyNo: "Survey No",
    aadharNo: "Aadhaar Number (12 digits)",
    panNo: "PAN Number (10 chars)",
    cropSelTitle: "Crop Selection (Select all applicable)",
    selected: "Selected",

    s3Title: "3) Bank & Nominee Details (Final Step)",
    bankName: "Bank Name",
    bankBranch: "Branch Name",
    accHolder: "Account Holder",
    accNo: "Account No. (8–18 digits)",
    ifsc: "IFSC (11 chars)",
    accType: "Account Type",

    nomineeName: "Nominee Name",
    nomineeAge: "Nominee Age",
    nomineeRel: "Nominee Relation",
    nomineeAddr: "Nominee Address",

    submitPay: "Submit & Pay ₹1000 (Auto Approve)",
    note: "Membership will be auto-approved only after successful payment verification.",

    orgBlockTitle: "Organization Link (Only for FPO / Cooperative Society)",
    orgSelect: "Select Registered Organization",
    orgSelectedName: "Selected Name",
    orgHint: "Your application will be forwarded to the selected organization dashboard.",
    select: "-- Select --",
    selectState: "-- Select State --",
    selectDistrict: "-- Select District --",
    selectTaluka: "-- Select Taluka --",
    selectGender: "-- Select --",
    selectCategory: "-- Select Category --",
    selectRel: "-- Select --",
    selectStateFirst: "Select State first",
    selectDistrictFirst: "Select District first",

    docsLaterTitle: "Important (Documents Upload)",
    docsLaterText:
      "Documents upload is not required now. After approval, login in Member Portal and upload anytime.",

    finalGateTitle: "Final Verification & Payment",
    finalGateSub:
      "This step works in sequence: Final Submit → OTP → Verify → Create Account → Pay.",
  },

  hi: {
    society: "शाइन एग्री अलाइड एंड मार्केटिंग मल्टीस्टेट को-ऑपरेटिव सोसायटी लिमिटेड",
    title: "किसान सदस्यता पंजीकरण",
    subtitle:
      "स्टेप-वाइज़ प्रोसेस: Form भरें → Final Submit पर OTP → OTP Verify → Password बनाएं → ₹1000 Pay (Auto Approval).",
    alreadyApplied: "पहले से आवेदन किया है?",
    payLaterTitle: "मोबाइल नंबर से Pay & Auto-Approve",
    payLaterSub: "मोबाइल → डिटेल्स → ₹1000 भुगतान → ऑटो अप्रूव",
    enterMobile: "मोबाइल नंबर दर्ज करें",
    fetchDetails: "डिटेल्स देखें",
    checking: "चेक हो रहा है...",
    name: "नाम",
    status: "स्थिति",
    fee: "फीस",
    payApprove: "₹1000 Pay करके Approve करें",
    processing: "प्रोसेसिंग...",

    chooseType: "सदस्यता प्रकार चुनें",
    regularTag: "रेगुलर",
    nominalTag: "नाममात्र",
    regularTitle: "रेगुलर सदस्य (शेयर के साथ)",
    nominalTitle: "नाममात्र सदस्य (शेयर बिना)",
    applyRegular: "रेगुलर के लिए आवेदन",
    applyNominal: "नाममात्र के लिए आवेदन",
    changeType: "टाइप बदलें",
    fixed: "फिक्स्ड",

    step1: "व्यक्तिगत व स्थान",
    step2: "किसान व KYC",
    step3: "बैंक, नामिनी व फाइनल",

    s1Title: "1) व्यक्तिगत विवरण व स्थान",
    fullName: "पूरा नाम",
    fatherName: "पिता का नाम",
    dob: "जन्म तिथि",
    gender: "लिंग",
    mobile: "मोबाइल",
    email: "Gmail ID",
    address: "पता",
    state: "राज्य",
    district: "जिला",
    taluka: "तालुका",
    village: "गाँव",
    occupation: "व्यवसाय",
    next: "आगे",
    back: "पीछे",

    otpTitle: "मोबाइल OTP सत्यापन",
    otpSub: "OTP केवल Final Submit पर (payment से पहले) आएगा।",
    sendOtp: "OTP भेजें",
    resendOtp: "OTP फिर भेजें",
    otpPH: "6 अंकों का OTP",
    verifyOtp: "Verify",
    verified: "Verified ✅",

    accountTitle: "Login Account बनाएं",
    password: "पासवर्ड बनाएं",
    confirmPassword: "पासवर्ड पुष्टि",
    create: "Account बनाएं",
    created: "Account Created ✅",
    accountNote: "Reset link Gmail पर जाएगा। Payment से पहले account जरूरी है।",

    s2Title: "2) किसान विवरण व KYC",
    farmerCategory: "किसान श्रेणी",
    landArea: "भूमि क्षेत्र (हेक्टेयर)",
    khataNo: "खाता नंबर",
    surveyNo: "सर्वे नंबर",
    aadharNo: "आधार नंबर (12 अंक)",
    panNo: "पैन नंबर (10 अक्षर)",
    cropSelTitle: "फसल चयन",
    selected: "चयनित",

    s3Title: "3) बैंक व नामिनी विवरण (अंतिम)",
    bankName: "बैंक नाम",
    bankBranch: "ब्रांच",
    accHolder: "खाता धारक",
    accNo: "खाता नंबर (8–18 अंक)",
    ifsc: "IFSC (11 अक्षर)",
    accType: "खाता प्रकार",

    nomineeName: "नामिनी नाम",
    nomineeAge: "नामिनी उम्र",
    nomineeRel: "रिश्ता",
    nomineeAddr: "नामिनी पता",

    submitPay: "सबमिट करें व ₹1000 Pay करें",
    note: "सदस्यता भुगतान सत्यापन के बाद ही ऑटो-अप्रूव होगी।",

    orgBlockTitle: "ऑर्ग लिंक (केवल FPO / Cooperative Society)",
    orgSelect: "रजिस्टर्ड ऑर्ग चुनें",
    orgSelectedName: "चयनित नाम",
    orgHint: "आपका आवेदन चुने हुए डैशबोर्ड पर भेज दिया जाएगा।",
    select: "-- चुनें --",
    selectState: "-- राज्य चुनें --",
    selectDistrict: "-- जिला चुनें --",
    selectTaluka: "-- तालुका चुनें --",
    selectGender: "-- चुनें --",
    selectCategory: "-- श्रेणी चुनें --",
    selectRel: "-- चुनें --",
    selectStateFirst: "पहले राज्य चुनें",
    selectDistrictFirst: "पहले जिला चुनें",

    docsLaterTitle: "महत्वपूर्ण (दस्तावेज़ अपलोड)",
    docsLaterText: "अभी अपलोड जरूरी नहीं। अप्रूवल के बाद Member Portal में अपलोड कर सकते हैं।",

    finalGateTitle: "फाइनल सत्यापन और भुगतान",
    finalGateSub: "क्रम: Final Submit → OTP → Verify → Account → Pay.",
  },

  gu: {
    society: "શાઇન એગ્રી એલાઇડ એન્ડ માર્કેટિંગ મલ્ટીસ્ટેટ સહકારી સંસ્થા લિ.",
    title: "ખેડૂત સભ્યપદ નોંધણી",
    subtitle:
      "સ્ટેપ-વાઈઝ: Form ભરો → Final Submit પર OTP → OTP Verify → Password બનાવો → ₹1000 Pay (Auto Approval).",
    alreadyApplied: "પહેલેથી અરજી કરી છે?",
    payLaterTitle: "મોબાઇલ નંબરથી Pay & Auto-Approve",
    payLaterSub: "મોબાઇલ → વિગતો → ₹1000 ચુકવણી → ઓટો એપ્રૂવ",
    enterMobile: "મોબાઇલ નંબર દાખલ કરો",
    fetchDetails: "વિગતો લાવો",
    checking: "ચેક થઈ રહ્યું છે...",
    name: "નામ",
    status: "સ્થિતિ",
    fee: "ફી",
    payApprove: "₹1000 Pay કરીને Approve કરો",
    processing: "પ્રોસેસિંગ...",

    chooseType: "સભ્યપદ પ્રકાર પસંદ કરો",
    regularTag: "રેગ્યુલર",
    nominalTag: "નોમિનલ",
    regularTitle: "રેગ્યુલર સભ્ય (શેર સાથે)",
    nominalTitle: "નોમિનલ સભ્ય (શેર વગર)",
    applyRegular: "રેગ્યુલર માટે અરજી",
    applyNominal: "નોમિનલ માટે અરજી",
    changeType: "ટાઇપ બદલો",
    fixed: "ફિક્સ",

    step1: "વ્યક્તિગત & સ્થાન",
    step2: "ખેડૂત & KYC",
    step3: "બેંક, નોમિની & ફાઈનલ",

    s1Title: "1) વ્યક્તિગત વિગતો & સ્થાન",
    fullName: "પૂર્ણ નામ",
    fatherName: "પિતાનું નામ",
    dob: "જન્મ તારીખ",
    gender: "લિંગ",
    mobile: "મોબાઇલ",
    email: "Gmail ID",
    address: "સરનામું",
    state: "રાજ્ય",
    district: "જિલ્લો",
    taluka: "તાલુકો",
    village: "ગામ",
    occupation: "વ્યવસાય",
    next: "આગળ",
    back: "પાછળ",

    otpTitle: "મોબાઇલ OTP ચકાસણી",
    otpSub: "OTP ફક્ત Final Submit પર (payment પહેલાં) આવશે.",
    sendOtp: "OTP મોકલો",
    resendOtp: "OTP ફરી મોકલો",
    otpPH: "6 અંક OTP",
    verifyOtp: "Verify",
    verified: "Verified ✅",

    accountTitle: "Login Account બનાવો",
    password: "પાસવર્ડ બનાવો",
    confirmPassword: "પાસવર્ડ પુષ્ટિ",
    create: "Account બનાવો",
    created: "Account Created ✅",
    accountNote: "Payment પહેલાં account ફરજિયાત છે.",

    s2Title: "2) ખેડૂત વિગતો & KYC",
    farmerCategory: "ખેડૂત કેટેગરી",
    landArea: "જમીન વિસ્તાર (હેક્ટર)",
    khataNo: "ખાતા નંબર",
    surveyNo: "સર્વે નંબર",
    aadharNo: "આધાર નંબર (12 અંક)",
    panNo: "PAN નંબર (10 અક્ષર)",
    cropSelTitle: "પાક પસંદગી",
    selected: "પસંદ થયેલ",

    s3Title: "3) બેંક & નોમિની વિગતો (અંતિમ)",
    bankName: "બેંકનું નામ",
    bankBranch: "બ્રાંચ",
    accHolder: "ખાતાધારક",
    accNo: "એકાઉન્ટ નંબર (8–18 અંક)",
    ifsc: "IFSC (11 અક્ષર)",
    accType: "એકાઉન્ટ પ્રકાર",

    nomineeName: "નોમિનીનું નામ",
    nomineeAge: "નોમિનીની ઉમર",
    nomineeRel: "સબંધ",
    nomineeAddr: "નોમિની સરનામું",

    submitPay: "સબમિટ કરો & ₹1000 Pay કરો",
    note: "ચુકવણી વેરિફિકેશન બાદ જ સભ્યપદ ઓટો-એપ્રૂવ થશે.",

    orgBlockTitle: "ઓર્ગ લિંક (ફક્ત FPO / Cooperative Society)",
    orgSelect: "રજિસ્ટર્ડ ઓર્ગ પસંદ કરો",
    orgSelectedName: "પસંદ થયેલ નામ",
    orgHint: "તમારી અરજી પસંદ કરેલા ડેશબોર્ડમાં મોકલાશે.",
    select: "-- પસંદ કરો --",
    selectState: "-- રાજ્ય પસંદ કરો --",
    selectDistrict: "-- જિલ્લો પસંદ કરો --",
    selectTaluka: "-- તાલુકો પસંદ કરો --",
    selectGender: "-- પસંદ કરો --",
    selectCategory: "-- કેટેગરી પસંદ કરો --",
    selectRel: "-- પસંદ કરો --",
    selectStateFirst: "પહેલા રાજ્ય પસંદ કરો",
    selectDistrictFirst: "પહેલા જિલ્લો પસંદ કરો",

    docsLaterTitle: "મહત્વપૂર્ણ (દસ્તાવેજ અપલોડ)",
    docsLaterText: "હમણાં અપલોડ જરૂરી નથી. એપ્રૂવલ પછી Member Portal માં અપલોડ કરી શકશો.",

    finalGateTitle: "ફાઈનલ ચકાસણી અને ચુકવણી",
    finalGateSub: "ક્રમ: Final Submit → OTP → Verify → Account → Pay.",
  },
};

export default function ApplyMembership() {
  // ✅ language
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");

  // ✅ STEP LOCK
  const [stepDone, setStepDone] = useState({ 1: false, 2: false, 3: false });

  // ✅ NEW: OTP/Account UI show only when Final Submit pressed
  const [showFinalOtpBox, setShowFinalOtpBox] = useState(false);

  // ✅ OTP states (FINAL SUBMIT only)
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);

  // ✅ Account create states (after OTP)
  const [accPassword, setAccPassword] = useState("");
  const [accConfirm, setAccConfirm] = useState("");
  const [accountCreating, setAccountCreating] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "lang" && e.newValue) setLang(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const t = (k) => {
    const v = I18N[lang]?.[k] || I18N.en[k];
    if (!v) pushMissingApplyKey(lang, k);
    return v || k;
  };

  const [formData, setFormData] = useState({
    membership_type: "",
    name: "",
    father_name: "",
    dob: "",
    gender: "",
    address: "",
    state: "",
    district: "",
    taluka: "",
    village: "",
    mobile: "",
    email: "",
    occupation: "",
    aadhar: "",
    pan: "",

    farmer_category: "",
    linked_org_uid: "",
    linked_org_name: "",

    land_area_hectare: "",
    land_khata_no: "",
    land_survey_no: "",
    crops: [],

    shares: "1",
    value_per_share: "100",
    total: "100",

    nominee_name: "",
    nominee_age: "",
    nominee_relation: "",
    nominee_address: "",

    status: "pending",
    membership_fee: FIXED_MEMBERSHIP_FEE,
    cooling_period_years: 5,

    bank_name: "",
    bank_branch: "",
    bank_account_holder: "",
    bank_account_no: "",
    bank_ifsc: "",
    bank_account_type: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [activeStep, setActiveStep] = useState(1);

  // portal settings
  const [portalSettings, setPortalSettings] = useState(null);
  const [portalLoading, setPortalLoading] = useState(true);
  const [portalError, setPortalError] = useState("");

  // org list
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState("");
  const [orgOptions, setOrgOptions] = useState([]);

  // Razorpay UI states
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  // pay later
  const [payMobile, setPayMobile] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookupMember, setLookupMember] = useState(null);

  const states = [
    "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
    "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
    "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
    "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  ];

  const CROP_OPTIONS = ["Pomegranate","Mango","Papaya","Potato","Coconut","Cluster Bean","Date Palm"];

  const STATE_DISTRICT_TALUKAS = {
    Gujarat: [
      { id: "Banaskantha", label: "Banaskantha", talukas: ["Amirgadh","Dhanera","Deesa","Danta","Dantiwada","Palanpur","Vadgam","Kankrej","Hadad","Ogad"] },
      { id: "Vav-Tharad", label: "Vav–Tharad District", talukas: ["Vav","Tharad","Suigam","Lakhni","Diyodar","Bhabhar","Dharnidhar","Rah"] },
      { id: "Kutch", label: "Kutch (Kachchh)", talukas: ["Abdasa","Anjar","Bhachau","Bhuj","Gandhidham","Lakhpat","Mandvi","Mundra","Nakhatrana","Rapar"] },
    ],
  };

  // =============================
  // Portal settings load (unchanged)
  // =============================
  useEffect(() => {
    const loadPortalSettings = async () => {
      setPortalLoading(true);
      setPortalError("");
      try {
        const refDoc = doc(db, "portal_settings", "global");
        const snap = await getDoc(refDoc);
        if (snap.exists()) {
          setPortalSettings(snap.data());
        } else {
          setPortalSettings({
            new_regular_open: true,
            new_nominal_open: true,
            member_login_open: true,
            dealer_portal_open: true,
          });
        }
      } catch (err) {
        console.error("Portal settings load error:", err);
        setPortalError(err.message || "Failed to load portal settings.");
        setPortalSettings(null);
      } finally {
        setPortalLoading(false);
      }
    };
    loadPortalSettings();
  }, []);

  const isRegular = formData.membership_type === "REGULAR";
  const isNominal = formData.membership_type === "NOMINAL";
  const isRegularOpen = portalSettings == null ? true : portalSettings.new_regular_open !== false;
  const isNominalOpen = portalSettings == null ? true : portalSettings.new_nominal_open !== false;
  const allClosed = !isRegularOpen && !isNominalOpen;

  const districtsForState = STATE_DISTRICT_TALUKAS[normalizeKey(formData.state)] || [];
  const selectedDistrictObj = districtsForState.find((d) => d.id === formData.district);
  const talukasForDistrict = selectedDistrictObj ? selectedDistrictObj.talukas : [];

  // =============================
  // Razorpay SDK loader (unchanged)
  // =============================
  function loadRazorpay() {
    return new Promise((resolve, reject) => {
      if (document.getElementById("razorpay-sdk")) return resolve(true);
      const s = document.createElement("script");
      s.id = "razorpay-sdk";
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => reject(new Error("Razorpay SDK failed to load"));
      document.body.appendChild(s);
    });
  }

  // =============================
  // ✅ OTP (FINAL SUBMIT only) - robust recaptcha
  // =============================
  const ensureRecaptcha = async () => {
    const id = "recaptcha-container-apply";
    const el = document.getElementById(id);
    if (!el) throw new Error("reCAPTCHA container missing");

    if (window.__apply_recaptcha__) return window.__apply_recaptcha__;

    try {
      if (el.childNodes && el.childNodes.length > 0) el.innerHTML = "";
    } catch {}

    window.__apply_recaptcha__ = new RecaptchaVerifier(auth, id, {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {
        try { window.__apply_recaptcha__?.clear?.(); } catch {}
        window.__apply_recaptcha__ = null;
        const node = document.getElementById(id);
        if (node) node.innerHTML = "";
      },
    });

    await window.__apply_recaptcha__.render();
    return window.__apply_recaptcha__;
  };

  const sendOtp = async () => {
    const mobile = String(formData.mobile || "").trim();
    if (!/^\d{10}$/.test(mobile)) return alert("Enter valid 10-digit mobile number.");

    setOtpLoading(true);
    try {
      const appVerifier = await ensureRecaptcha();
      const result = await signInWithPhoneNumber(auth, `+91${mobile}`, appVerifier);
      setConfirmResult(result);
      setOtpSent(true);
      setOtpVerified(false);
      alert("✅ OTP sent (enter latest OTP)");
    } catch (e) {
      alert("❌ OTP send failed: " + (e.message || e));
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!confirmResult) return alert("Send OTP first");
    const cleanOtp = String(otp || "").trim();
    if (!/^\d{6}$/.test(cleanOtp)) return alert("Enter valid 6-digit OTP");

    setOtpLoading(true);
    try {
      await confirmResult.confirm(cleanOtp);
      setOtpVerified(true);
      alert("✅ Mobile verified");
    } catch (e) {
      setOtpVerified(false);
      alert("❌ OTP verify failed: " + (e.message || e));
    } finally {
      setOtpLoading(false);
    }
  };

  // =============================
  // ✅ Create Gmail + Password account (after OTP)
  // =============================
  const createLoginAccount = async () => {
    const email = String(formData.email || "").trim();
    const pass = String(accPassword || "");
    const pass2 = String(accConfirm || "");

    if (!otpVerified) return alert("Please verify OTP first.");
    if (!email) return alert("Please enter Gmail ID.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return alert("Please enter valid Email ID.");
    if (pass.length < 6) return alert("Password must be minimum 6 characters.");
    if (pass !== pass2) return alert("Password and Confirm Password must match.");

    setAccountCreating(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, pass);
      if (!cred?.user?.uid) throw new Error("Account not created");
      setAccountCreated(true);
      alert("✅ Gmail + Password account created successfully.");
    } catch (e) {
      alert("❌ Account create failed: " + (e?.message || e));
      setAccountCreated(false);
    } finally {
      setAccountCreating(false);
    }
  };

  // =============================
  // Handlers
  // =============================
  const resetFinalGate = () => {
    setShowFinalOtpBox(false);
    setOtpSent(false);
    setOtp("");
    setOtpVerified(false);
    setConfirmResult(null);
    setAccountCreated(false);
    setAccPassword("");
    setAccConfirm("");
    setAccountCreating(false);
  };

  const startAsType = (type) => {
    if (type === "REGULAR" && !isRegularOpen) return alert("Regular closed by admin.");
    if (type === "NOMINAL" && !isNominalOpen) return alert("Nominal closed by admin.");

    setSelectedType(type);
    setActiveStep(1);
    setStepDone({ 1: false, 2: false, 3: false });
    resetFinalGate();

    setFormData((prev) => {
      const next = { ...prev, membership_type: type };
      if (type === "REGULAR") {
        const shares = Number(next.shares || 1);
        const vps = Number(next.value_per_share || 100);
        next.shares = String(shares);
        next.value_per_share = String(vps);
        next.total = String(shares * vps);
      } else {
        next.shares = "0";
        next.value_per_share = "0";
        next.total = "0";
      }
      return next;
    });
  };

  const goStep = (n) => {
    if (n === 1) return setActiveStep(1);
    if (n === 2) {
      if (!stepDone[1]) return;
      return setActiveStep(2);
    }
    if (n === 3) {
      if (!stepDone[2]) return;
      return setActiveStep(3);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "state") {
      setFormData((prev) => ({ ...prev, state: value, district: "", taluka: "" }));
      return;
    }
    if (name === "district") {
      setFormData((prev) => ({ ...prev, district: value, taluka: "" }));
      return;
    }
    if (name === "farmer_category") {
      setFormData((prev) => ({
        ...prev,
        farmer_category: value,
        linked_org_uid: "",
        linked_org_name: "",
      }));
      return;
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "shares" || name === "value_per_share") {
        const shares = +updated.shares || 0;
        const vps = +updated.value_per_share || 0;
        updated.total = String(shares * vps);
      }
      return updated;
    });
  };

  const toggleCrop = (crop) => {
    setFormData((prev) => {
      const exists = prev.crops?.includes(crop);
      const nextCrops = exists
        ? prev.crops.filter((c) => c !== crop)
        : [...(prev.crops || []), crop];
      return { ...prev, crops: nextCrops };
    });
  };

  // =============================
  // Org list load (unchanged)
  // =============================
  useEffect(() => {
    const loadOrgs = async () => {
      const isOrgCategory =
        formData.farmer_category === "FPO" ||
        formData.farmer_category === "Cooperative Society";

      if (!isOrgCategory) {
        setOrgOptions([]);
        setOrgError("");
        setOrgLoading(false);
        return;
      }

      setOrgLoading(true);
      setOrgError("");
      try {
        const qRef = query(collection(db, "farmer_vendors"), where("status", "==", "approved"));
        const snap = await getDocs(qRef);

        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          const name =
            data.name || data.company_name || data.org_name || data.fpo_name || "Unnamed";
          const ttype = String(
            data.farmer_category || data.vendor_type || data.type || ""
          ).toUpperCase();
          return { uid: d.id, name, type: ttype };
        });

        const filteredList = list.filter((x) => {
          if (formData.farmer_category === "FPO") return x.type.includes("FPO");
          return x.type.includes("COOPERATIVE") || x.type.includes("SOCIETY");
        });

        setOrgOptions(filteredList);
      } catch (e) {
        console.error("org load error", e);
        setOrgError(e.message || "Failed to load organizations");
        setOrgOptions([]);
      } finally {
        setOrgLoading(false);
      }
    };
    loadOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.farmer_category]);

  const handleOrgSelect = (e) => {
    const uid = e.target.value;
    const org = orgOptions.find((o) => o.uid === uid);
    setFormData((prev) => ({
      ...prev,
      linked_org_uid: uid,
      linked_org_name: org ? org.name : "",
    }));
  };

  /* =============================
     Govt validations (required)
  ============================== */
  const isAadhaarValid = (x) => /^\d{12}$/.test(String(x || "").trim());
  const isPanValid = (x) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(String(x || "").trim());
  const isIfscValid = (x) => /^[A-Z]{4}0[A-Z0-9]{6}$/i.test(String(x || "").trim());
  const isAccountValid = (x) => /^\d{8,18}$/.test(String(x || "").trim());
  const isKhataSurveyValid = (x) => /^[0-9A-Za-z\/\-\s]{1,30}$/.test(String(x || "").trim());

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.name || !formData.father_name || !formData.dob || !formData.gender) {
        alert("Please fill Name, Father, DOB, Gender.");
        return false;
      }
      if (!formData.address || !formData.state || !formData.district || !formData.taluka || !formData.village) {
        alert("Please fill Address + State/District/Taluka/Village.");
        return false;
      }
      if (!/^\d{10}$/.test(String(formData.mobile || "").trim())) {
        alert("Please enter valid 10-digit mobile number.");
        return false;
      }
      if (!String(formData.email || "").trim()) {
        alert("Please enter Gmail ID.");
        return false;
      }
      return true;
    }

    if (step === 2) {
      if (!formData.farmer_category) {
        alert("Please select Farmer Category.");
        return false;
      }

      const isOrgCategory =
        formData.farmer_category === "FPO" ||
        formData.farmer_category === "Cooperative Society";
      if (isOrgCategory && !formData.linked_org_uid) {
        alert("Please select registered organization.");
        return false;
      }

      if (!formData.land_area_hectare || Number(formData.land_area_hectare) <= 0) {
        alert("Please enter Land Area (Hectare).");
        return false;
      }

      if (!formData.land_khata_no || !isKhataSurveyValid(formData.land_khata_no)) {
        alert("Khata No required (alphanumeric / / - allowed).");
        return false;
      }
      if (!formData.land_survey_no || !isKhataSurveyValid(formData.land_survey_no)) {
        alert("Survey No required (alphanumeric / / - allowed).");
        return false;
      }

      if (!isAadhaarValid(formData.aadhar)) {
        alert("Aadhaar number must be 12 digits.");
        return false;
      }

      if (String(formData.pan || "").trim() && !isPanValid(formData.pan)) {
        alert("PAN must be 10 characters (Example: ABCDE1234F).");
        return false;
      }

      if (!formData.crops || formData.crops.length === 0) {
        alert("Please select at least one crop.");
        return false;
      }

      return true;
    }

    if (step === 3) {
      if (!formData.bank_name || !formData.bank_account_holder) {
        alert("Please fill Bank Name and Account Holder.");
        return false;
      }
      if (!isAccountValid(formData.bank_account_no)) {
        alert("Account Number must be 8 to 18 digits.");
        return false;
      }
      if (!isIfscValid(formData.bank_ifsc)) {
        alert("IFSC must be 11 characters (Example: HDFC0ABCDE12).");
        return false;
      }
      return true;
    }

    return true;
  };

  // =============================
  // Create Razorpay order (unchanged)
  // =============================
  const createRazorpayOrder = async ({ amount, memberDocId, purpose }) => {
    const res = await fetch(CREATE_ORDER_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amountPaise: Number(amount || 0) * 100,
        receipt: `MEM-${memberDocId || "NA"}-${Date.now()}`,
        notes: { purpose: purpose || "MEMBERSHIP_FEE", memberDocId: memberDocId || "" },
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.error) throw new Error(data?.error || "Failed to create Razorpay order.");

    if (data?.order?.id) return { order: data.order, key: data.key || RAZORPAY_KEY_ID };
    if (data?.id) return { order: { id: data.id }, key: data.key || RAZORPAY_KEY_ID };

    throw new Error("Order creation response missing id.");
  };

  // =============================
  // Wait Paid/Approved (fixed)
  // =============================
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const waitForPaidApproved = async (memberDocId, timeoutMs = 120000) => {
    const started = Date.now();
    const memberRef = doc(db, "members", memberDocId);

    while (Date.now() - started < timeoutMs) {
      const snap = await getDoc(memberRef);
      const member = snap.exists() ? snap.data() : null;

      const paid = String(member?.payment_status || "").toUpperCase() === "PAID";
      const approved = String(member?.status || "").toLowerCase() === "approved";

      if (paid && approved) return member;
      await sleep(2500);
    }

    throw new Error("Payment is taking longer than expected. Please use Pay Later by mobile to re-check.");
  };

  // =============================
  // Start Razorpay (unchanged)
  // =============================
  const startRazorpayPayment = async ({ memberDocId, memberName, memberMobile, memberEmail }) => {
    setPayError("");
    setPaying(true);

    try {
      await loadRazorpay();

      const orderResp = await createRazorpayOrder({
        amount: FIXED_MEMBERSHIP_FEE,
        memberDocId,
        purpose: "MEMBERSHIP_FEE",
      });

      const order = orderResp.order || orderResp;
      const orderId = order?.id || order?.orderId;

      if (!orderId) throw new Error("Order ID missing. Please retry payment.");
      if (!orderResp.key) orderResp.key = RAZORPAY_KEY_ID;

      const options = {
        key: orderResp.key,
        amount: order?.amount || FIXED_MEMBERSHIP_FEE * 100,
        currency: order?.currency || "INR",
        name: I18N[lang]?.society || I18N.en.society,
        description: "Membership Fee Payment",
        order_id: orderId,
        image: "https://shineagri.org/logo.png",
        prefill: { name: memberName || "", email: memberEmail || "", contact: memberMobile || "" },
        notes: { memberDocId, purpose: "MEMBERSHIP_FEE" },
        theme: { color: "#059669" },
        handler: async (response) => {
          try {
            try {
              await verifyAndApprove({ memberDocId, rp: response });
            } catch (e) {
              console.warn("verifyAndApprove failed, switching to Firestore confirmation:", e?.message);
              await waitForPaidApproved(memberDocId, 120000);
            }

            alert("✅ Payment Success!\nMembership Approved.\n\nNow login with Gmail + Password in Member Portal.");
            window.location.reload();
          } catch (err) {
            console.error("final approval flow error:", err);
            alert(err?.message || "Payment done but confirmation pending. Please use Pay Later after 2 minutes.");
            setPaying(false);
          }
        },
      };

      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (resp) {
        console.error("payment.failed", resp);
        setPaying(false);
        alert("Payment not completed. If amount is debited, membership may auto-approve within 1–3 minutes. Use 'Pay Later' to retry if needed.");
      });
      rzp.open();
    } catch (e) {
      console.error("startRazorpayPayment error:", e);
      setPayError(e.message || "Payment start failed.");
      setPaying(false);
    }
  };

  // =============================
  // Lookup (pay later)
  // =============================
  const lookupByMobile = async () => {
    const mobile = String(payMobile || "").trim();
    if (!/^\d{10}$/.test(mobile)) return alert("Please enter valid 10-digit mobile number.");

    setLookupLoading(true);
    setLookupError("");
    setLookupMember(null);

    try {
      const qRef = query(collection(db, "members"), where("mobile", "==", mobile));
      const snap = await getDocs(qRef);

      if (snap.empty) {
        setLookupError("No application found for this mobile number.");
        return;
      }

      const docs = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      docs.sort((a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0));

      setLookupMember(docs[0]);
    } catch (e) {
      console.error("lookup error", e);
      setLookupError(e.message || "Lookup failed.");
    } finally {
      setLookupLoading(false);
    }
  };

  // =============================
  // FINAL CTA label
  // =============================
  const finalCtaLabel = useMemo(() => {
    if (submitting || paying) return t("processing");
    if (!otpSent) return "✅ Final Submit → Send OTP";
    if (otpSent && !otpVerified) return "🔐 Verify OTP (then Create Account)";
    if (otpVerified && !accountCreated) return "👤 Create Account (then Pay)";
    return `💳 ${t("submitPay")}`;
  }, [otpSent, otpVerified, accountCreated, submitting, paying, t]);

  // =============================
  // Submit (STRICT FINAL GATE)
  // =============================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || paying) return;

    if (!formData.membership_type) return alert("Please select membership type first.");
    if (isRegular && !isRegularOpen) return alert("Regular Membership registration is closed.");
    if (isNominal && !isNominalOpen) return alert("Nominal Membership registration is closed.");

    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) return;

    const mobileKey = String(formData.mobile || "").trim();
    const aadhaarKey = String(formData.aadhar || "").trim();

    // Duplicate checks BEFORE OTP
    const qMobile = query(collection(db, "members"), where("mobile", "==", mobileKey));
    const snapMobile = await getDocs(qMobile);
    if (!snapMobile.empty) {
      const existing = snapMobile.docs[0].data() || {};
      const paid = String(existing.payment_status || "").toUpperCase() === "PAID";
      const approved = String(existing.status || "").toLowerCase() === "approved";
      if (paid && approved)
        alert("⚠️ इस Mobile Number से पहले से Membership APPROVED है.\nकृपया Member Portal में Login करें.");
      else
        alert("⚠️ इस Mobile Number से पहले से Application PENDING है.\nकृपया 'Pay Later' से payment complete करें.");
      return;
    }

    const qAadhaar = query(collection(db, "members"), where("aadhar", "==", aadhaarKey));
    const snapAadhaar = await getDocs(qAadhaar);
    if (!snapAadhaar.empty) {
      const existing = snapAadhaar.docs[0].data() || {};
      const paid = String(existing.payment_status || "").toUpperCase() === "PAID";
      const approved = String(existing.status || "").toLowerCase() === "approved";
      if (paid && approved)
        alert("⚠️ इस Aadhaar Number से पहले से Membership APPROVED है.\nकृपया Member Portal में Login करें.");
      else
        alert("⚠️ इस Aadhaar Number से पहले से Application PENDING है.\nकृपया 'Pay Later' से payment complete करें.");
      return;
    }

    // ✅ Final Submit triggers OTP UI + Send OTP
    if (!otpSent) {
      setShowFinalOtpBox(true);
      await sendOtp();
      return;
    }

    if (!otpVerified) {
      setShowFinalOtpBox(true);
      alert("Please verify OTP to continue.");
      return;
    }

    if (!accountCreated) {
      alert("Please create account (Gmail + Password) to continue.");
      return;
    }

    const uid = auth.currentUser?.uid; // after createUserWithEmailAndPassword
    if (!uid) {
      alert("Login account not found. Please create Gmail + Password again.");
      return;
    }

    const cleaned = {
      ...formData,
      name: formData.name.trim(),
      father_name: formData.father_name.trim(),
      address: formData.address.trim(),
      state: formData.state.trim(),
      district: formData.district.trim(),
      taluka: formData.taluka.trim(),
      village: formData.village.trim(),
      mobile: formData.mobile.trim(),
      email: formData.email.trim(),
      occupation: formData.occupation.trim(),
      aadhar: formData.aadhar.trim(),
      pan: String(formData.pan || "").trim().toUpperCase(),
      farmer_category: formData.farmer_category.trim(),
      linked_org_uid: formData.linked_org_uid || "",
      linked_org_name: formData.linked_org_name || "",
      land_area_hectare: String(formData.land_area_hectare || "").trim(),
      land_khata_no: String(formData.land_khata_no || "").trim(),
      land_survey_no: String(formData.land_survey_no || "").trim(),
      nominee_name: String(formData.nominee_name || "").trim(),
      nominee_relation: String(formData.nominee_relation || "").trim(),
      nominee_address: String(formData.nominee_address || "").trim(),
      bank_name: String(formData.bank_name || "").trim(),
      bank_branch: String(formData.bank_branch || "").trim(),
      bank_account_holder: String(formData.bank_account_holder || "").trim(),
      bank_account_no: String(formData.bank_account_no || "").trim(),
      bank_ifsc: String(formData.bank_ifsc || "").trim().toUpperCase(),
      bank_account_type: String(formData.bank_account_type || "").trim(),
      crops: Array.isArray(formData.crops) ? formData.crops : [],
      membership_fee: FIXED_MEMBERSHIP_FEE,
      cooling_period_years: isNominal ? 5 : null,
    };

    setSubmitting(true);
    try {
      const finalDoc = {
        ...cleaned,
        preferred_lang: localStorage.getItem("lang") || "en",
        member_id: "APPLIED",
        status: "pending",
        created_at: new Date().toISOString(),
        createdAt: serverTimestamp(),
        payment_status: "NOT_PAID",
        documents: {}, // apply-time empty
        auth_uid: uid, // ✅ for secure upload rules
      };

      const docRef = await addDoc(collection(db, "members"), finalDoc);

      const isOrgCategory =
        String(cleaned.farmer_category || "").toUpperCase() === "FPO" ||
        String(cleaned.farmer_category || "").toUpperCase() === "COOPERATIVE SOCIETY";

      if (isOrgCategory && cleaned.linked_org_uid) {
        await setDoc(
          doc(db, "farmer_vendors", cleaned.linked_org_uid, "member_requests", docRef.id),
          {
            memberDocId: docRef.id,
            status: "pending",
            createdAt: serverTimestamp(),
            from_member_name: cleaned.name || "",
            from_member_mobile: cleaned.mobile || "",
            from_member_email: cleaned.email || "",
            linked_org_uid: cleaned.linked_org_uid,
            linked_org_name: cleaned.linked_org_name,
            membership_fee: FIXED_MEMBERSHIP_FEE,
            membership_type: cleaned.membership_type || "",
            farmer_category: cleaned.farmer_category || "",
            preferred_lang: cleaned.preferred_lang || localStorage.getItem("lang") || "en",
          },
          { merge: true }
        );
      }

      await startRazorpayPayment({
        memberDocId: docRef.id,
        memberName: cleaned.name,
        memberMobile: cleaned.mobile,
        memberEmail: cleaned.email,
      });

      setSubmitting(false);
    } catch (err) {
      console.error("❌ Submission Error:", err);
      alert("Error submitting form: " + (err.message || err));
      setSubmitting(false);
    }
  };

  // =============================
  // UI
  // =============================
  return (
    <div className="min-h-screen bg-[radial-gradient(1200px_520px_at_50%_-120px,#dbeafe_0%,#eef2ff_35%,#ffffff_100%)] pt-[140px] pb-10">
      {/* ✅ Keep reCAPTCHA container ALWAYS mounted */}
      <div id="recaptcha-container-apply" className="hidden" />

      <div className="max-w-6xl mx-auto px-4">
        {/* Header + Language Switch */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-slate-200 text-slate-700 text-xs font-extrabold shadow-sm mb-2">
                🌿 {t("society")}
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                {t("title")}
              </h1>
              <p className="text-slate-600 max-w-3xl text-xs md:text-sm mt-1">
                {t("subtitle")}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {["en", "hi", "gu"].map((L) => (
                <button
                  key={L}
                  type="button"
                  onClick={() => setLang(L)}
                  className={cx(
                    "px-3 py-1.5 rounded-full text-[12px] font-extrabold border shadow-sm",
                    lang === L
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  )}
                >
                  {L.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Pay Later */}
        <div className="mb-6 bg-white rounded-2xl shadow-[0_14px_40px_rgba(15,23,42,0.08)] border border-slate-200 p-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                {t("alreadyApplied")}
              </div>
              <h2 className="text-base font-extrabold text-slate-900">{t("payLaterTitle")}</h2>
              <p className="text-[12px] text-slate-600 mt-1">{t("payLaterSub")}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <input
                value={payMobile}
                onChange={(e) => setPayMobile(e.target.value)}
                placeholder={t("enterMobile")}
                inputMode="numeric"
                maxLength={10}
                className="w-full sm:w-64 border border-slate-300 rounded-lg px-3 py-2 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button
                type="button"
                onClick={lookupByMobile}
                disabled={lookupLoading}
                className="px-5 py-2 rounded-lg bg-slate-900 text-white text-[13px] font-extrabold hover:bg-slate-800 disabled:opacity-60"
              >
                {lookupLoading ? t("checking") : t("fetchDetails")}
              </button>
            </div>
          </div>

          {lookupError && (
            <div className="mt-3 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
              {lookupError}
            </div>
          )}

          {lookupMember && (
            <div className="mt-4 border border-slate-200 rounded-xl p-4 bg-slate-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[11px] text-slate-500">{t("name")}</div>
                  <div className="font-semibold text-slate-900">{lookupMember.name || "-"}</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">{t("status")}</div>
                  <div className="font-semibold text-slate-900">
                    {String(lookupMember.status || "-")} / {String(lookupMember.payment_status || "-")}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">{t("fee")}</div>
                  <div className="font-semibold text-slate-900">₹{FIXED_MEMBERSHIP_FEE}</div>
                </div>
              </div>

              <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-xs text-slate-600">
                  ₹{FIXED_MEMBERSHIP_FEE} payment is required to approve membership.
                </div>

                <button
                  type="button"
                  disabled={paying}
                  onClick={() =>
                    startRazorpayPayment({
                      memberDocId: lookupMember.id,
                      memberName: lookupMember.name,
                      memberMobile: lookupMember.mobile,
                      memberEmail: lookupMember.email,
                    })
                  }
                  className="inline-flex items-center justify-center gap-2 bg-emerald-600 text-white font-extrabold px-6 py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-60"
                >
                  {paying ? t("processing") : t("payApprove")}
                </button>
              </div>

              {payError && (
                <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
                  {payError}
                </div>
              )}
            </div>
          )}
        </div>

        {portalError && (
          <div className="mb-4 text-[12px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
            ⚠ Unable to read portal settings from Firestore. Default behaviour will treat all portals as open.
            <div className="font-mono mt-1">{portalError}</div>
          </div>
        )}

        {/* Choose Type */}
        {!selectedType && (
          <div className="bg-white rounded-2xl shadow-[0_18px_55px_rgba(15,23,42,0.10)] border border-slate-200 p-5 md:p-7">
            {portalLoading ? (
              <div className="text-[13px] text-slate-500">Checking membership registration status…</div>
            ) : allClosed ? (
              <div className="text-[13px] text-slate-700">
                🔒 Online membership registration is currently <b>closed by the Society Admin</b>.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="text-base md:text-lg font-extrabold text-slate-900">{t("chooseType")}</h2>
                  <div className="text-[12px] text-slate-600">
                    Fee: <b>₹{FIXED_MEMBERSHIP_FEE}</b> ({t("fixed")})
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className={cx("rounded-2xl p-5 border shadow-sm", isRegularOpen ? "bg-emerald-50/60 border-emerald-200" : "bg-slate-100/70 border-slate-200 opacity-70")}>
                    <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-bold mb-1">{t("regularTag")}</div>
                    <div className="text-base font-extrabold text-slate-900 mb-2">{t("regularTitle")}</div>
                    <button
                      type="button"
                      onClick={() => startAsType("REGULAR")}
                      disabled={!isRegularOpen}
                      className={cx(
                        "w-full md:w-auto inline-flex items-center justify-center px-5 py-2 rounded-full text-[13px] font-extrabold",
                        isRegularOpen ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-300 text-slate-600 cursor-not-allowed"
                      )}
                    >
                      {t("applyRegular")}
                    </button>
                  </div>

                  <div className={cx("rounded-2xl p-5 border shadow-sm", isNominalOpen ? "bg-amber-50/60 border-amber-200" : "bg-slate-100/70 border-slate-200 opacity-70")}>
                    <div className="text-[11px] uppercase tracking-wide text-amber-700 font-bold mb-1">{t("nominalTag")}</div>
                    <div className="text-base font-extrabold text-slate-900 mb-2">{t("nominalTitle")}</div>
                    <button
                      type="button"
                      onClick={() => startAsType("NOMINAL")}
                      disabled={!isNominalOpen}
                      className={cx(
                        "w-full md:w-auto inline-flex items-center justify-center px-5 py-2 rounded-full text-[13px] font-extrabold",
                        isNominalOpen ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-slate-300 text-slate-600 cursor-not-allowed"
                      )}
                    >
                      {t("applyNominal")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Form */}
        {selectedType && (
          <div className="mt-6 bg-white/90 backdrop-blur rounded-2xl shadow-[0_18px_55px_rgba(15,23,42,0.10)] border border-slate-200 overflow-visible">
            {/* top banner */}
            <div className="px-5 md:px-7 py-3 bg-gradient-to-r from-emerald-700 via-teal-700 to-sky-700 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-emerald-100 mb-0.5">Membership Type Selected</div>
                <div className="text-[13px] md:text-sm font-extrabold">
                  {isRegular ? t("regularTitle") : t("nominalTitle")}
                </div>
              </div>

              <div className="flex items-center gap-3 text-[12px]">
                <span>💳 Fee: <b>₹{FIXED_MEMBERSHIP_FEE}</b> ({t("fixed")})</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedType(null);
                    setActiveStep(1);
                    setStepDone({ 1: false, 2: false, 3: false });
                    setFormData((prev) => ({ ...prev, membership_type: "" }));
                    resetFinalGate();
                  }}
                  className="px-3 py-1 rounded-full bg-white/10 border border-emerald-200/60 text-emerald-50 hover:bg-white/20"
                >
                  {t("changeType")}
                </button>
              </div>
            </div>

            {/* step nav (strict lock) */}
            <div className="px-5 md:px-7 py-4 bg-white border-b border-slate-200 flex flex-wrap gap-2 sticky top-[160px] z-30">
              <StepBtn activeStep={activeStep} n={1} title={t("step1")} onClick={() => goStep(1)} />
              <StepBtn activeStep={activeStep} n={2} title={t("step2")} locked={!stepDone[1]} onClick={() => goStep(2)} />
              <StepBtn activeStep={activeStep} n={3} title={t("step3")} locked={!stepDone[2]} onClick={() => goStep(3)} />
            </div>

            {/* MAIN FORM */}
            <form onSubmit={handleSubmit} className="px-5 md:px-7 py-6 grid grid-cols-1 gap-5 text-[13px] bg-slate-50/40">
              {/* STEP 1 */}
              {activeStep === 1 && (
                <SectionCard title={t("s1Title")} sub={t("subtitle")}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block font-extrabold mb-1">{t("fullName")} *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("fatherName")} *</label>
                      <input type="text" name="father_name" value={formData.father_name} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("dob")} *</label>
                      <input type="date" name="dob" value={formData.dob} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("gender")} *</label>
                      <select name="gender" value={formData.gender} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <option value="">{t("selectGender")}</option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("mobile")} *</label>
                      <input type="text" name="mobile" value={formData.mobile} onChange={handleChange} required inputMode="numeric" maxLength={10}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                      <FieldHint>OTP will come only at Final Submit (Step 3).</FieldHint>
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("email")} *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                      <FieldHint>Reset link will be sent to this Gmail.</FieldHint>
                    </div>

                    <div className="md:col-span-3">
                      <label className="block font-extrabold mb-1">{t("address")} *</label>
                      <textarea name="address" value={formData.address} onChange={handleChange} required rows={2}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("state")} *</label>
                      <select name="state" value={formData.state} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <option value="">{t("selectState")}</option>
                        {states.map((s, i) => (<option key={i} value={s}>{s}</option>))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("district")} *</label>
                      <select name="district" value={formData.district} onChange={handleChange} disabled={!districtsForState.length}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <option value="">
                          {districtsForState.length ? t("selectDistrict") : t("selectStateFirst")}
                        </option>
                        {districtsForState.map((d) => (<option key={d.id} value={d.id}>{d.label}</option>))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("taluka")} *</label>
                      <select name="taluka" value={formData.taluka} onChange={handleChange} disabled={!talukasForDistrict.length}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <option value="">
                          {talukasForDistrict.length ? t("selectTaluka") : t("selectDistrictFirst")}
                        </option>
                        {talukasForDistrict.map((ta) => (<option key={ta} value={ta}>{ta}</option>))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("village")} *</label>
                      <input type="text" name="village" value={formData.village} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("occupation")} *</label>
                      <select name="occupation" value={formData.occupation} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <option value="">{t("select")}</option>
                        <option>Farmer</option>
                        <option>Laborer</option>
                        <option>Trader</option>
                        <option>Student</option>
                        <option>Businessman</option>
                        <option>Housewife</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        if (!validateStep(1)) return;
                        setStepDone((p) => ({ ...p, 1: true }));
                        setActiveStep(2);
                      }}
                      className="px-7 py-2.5 rounded-full bg-emerald-600 text-white text-[13px] font-extrabold hover:bg-emerald-700"
                    >
                      ➕ {t("next")} → {t("step2")}
                    </button>
                  </div>
                </SectionCard>
              )}

              {/* STEP 2 */}
              {activeStep === 2 && (
                <SectionCard title={t("s2Title")} sub="Govt validations: Aadhaar/PAN/Khata/Survey">
                  {/* Org Link */}
                  <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 mb-5">
                    <div className="font-extrabold text-slate-900 mb-2 text-[13px]">{t("orgBlockTitle")}</div>

                    {formData.farmer_category === "FPO" || formData.farmer_category === "Cooperative Society" ? (
                      <>
                        {orgLoading ? (
                          <div className="text-[12px] text-slate-600">Loading registered organizations…</div>
                        ) : orgError ? (
                          <div className="text-[12px] text-rose-600">{orgError}</div>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block font-extrabold mb-1">
                                  {t("orgSelect")} <span className="text-rose-600">*</span>
                                </label>
                                <select
                                  value={formData.linked_org_uid}
                                  onChange={handleOrgSelect}
                                  className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                >
                                  <option value="">{t("select")}</option>
                                  {orgOptions.map((o) => (
                                    <option key={o.uid} value={o.uid}>{o.name}</option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block font-extrabold mb-1">{t("orgSelectedName")}</label>
                                <input
                                  value={formData.linked_org_name || ""}
                                  readOnly
                                  className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-slate-100 text-slate-700"
                                />
                              </div>
                            </div>

                            <div className="mt-2 text-[11px] text-slate-600">
                              ✅ Fee fixed: <b>₹{FIXED_MEMBERSHIP_FEE}</b> • ✅ {t("orgHint")}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="text-[12px] text-slate-600">
                        This section applies only if you choose <b>FPO</b> or <b>Cooperative Society</b>.
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                    <div>
                      <label className="block font-extrabold mb-1">{t("farmerCategory")} *</label>
                      <select
                        name="farmer_category"
                        value={formData.farmer_category}
                        onChange={handleChange}
                        required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="">{t("selectCategory")}</option>
                        <option>Farmer</option>
                        <option>FPO</option>
                        <option>SHG</option>
                        <option>Cooperative Society</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("landArea")} *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        name="land_area_hectare"
                        value={formData.land_area_hectare}
                        onChange={handleChange}
                        required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("khataNo")} *</label>
                      <input
                        type="text"
                        name="land_khata_no"
                        value={formData.land_khata_no}
                        onChange={handleChange}
                        required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <FieldHint>Allowed: alphanumeric / / -</FieldHint>
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("surveyNo")} *</label>
                      <input
                        type="text"
                        name="land_survey_no"
                        value={formData.land_survey_no}
                        onChange={handleChange}
                        required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("aadharNo")} *</label>
                      <input
                        type="text"
                        name="aadhar"
                        value={formData.aadhar}
                        onChange={handleChange}
                        required
                        inputMode="numeric"
                        maxLength={12}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <FieldHint>Must be 12 digits</FieldHint>
                    </div>

                    <div>
                      <label className="block font-extrabold mb-1">{t("panNo")}</label>
                      <input
                        type="text"
                        name="pan"
                        value={formData.pan}
                        onChange={handleChange}
                        maxLength={10}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <FieldHint>Example: ABCDE1234F</FieldHint>
                    </div>
                  </div>

                  {/* Crops */}
                  <div className="border border-slate-200 rounded-xl p-4 bg-white mb-5">
                    <div className="font-extrabold text-slate-900 mb-2 text-[13px]">{t("cropSelTitle")} *</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {CROP_OPTIONS.map((c) => {
                        const checked = formData.crops?.includes(c);
                        return (
                          <label
                            key={c}
                            className={checked
                              ? "flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 cursor-pointer"
                              : "flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 cursor-pointer hover:bg-slate-100"
                            }
                          >
                            <input type="checkbox" checked={checked} onChange={() => toggleCrop(c)} />
                            <span className="text-[13px] font-semibold">{c}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-2">
                      {t("selected")}: <b>{(formData.crops || []).join(", ") || "-"}</b>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setActiveStep(1)}
                      className="px-6 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-[13px] font-extrabold hover:bg-slate-50"
                    >
                      ← {t("back")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!validateStep(2)) return;
                        setStepDone((p) => ({ ...p, 2: true }));
                        setActiveStep(3);
                      }}
                      className="px-7 py-2.5 rounded-full bg-emerald-600 text-white text-[13px] font-extrabold hover:bg-emerald-700"
                    >
                      ➕ {t("next")} → {t("step3")}
                    </button>
                  </div>
                </SectionCard>
              )}

              {/* STEP 3 */}
              {activeStep === 3 && (
                <SectionCard title={t("s3Title")} sub={t("finalGateSub")}>
                  {/* Bank */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                    <div>
                      <label className="block font-extrabold mb-1">{t("bankName")} *</label>
                      <input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div>
                      <label className="block font-extrabold mb-1">{t("bankBranch")}</label>
                      <input type="text" name="bank_branch" value={formData.bank_branch} onChange={handleChange}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div>
                      <label className="block font-extrabold mb-1">{t("accHolder")} *</label>
                      <input type="text" name="bank_account_holder" value={formData.bank_account_holder} onChange={handleChange} required
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div>
                      <label className="block font-extrabold mb-1">{t("accNo")} *</label>
                      <input type="text" name="bank_account_no" value={formData.bank_account_no} onChange={handleChange} required inputMode="numeric" maxLength={18}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                      <FieldHint>8 to 18 digits</FieldHint>
                    </div>
                    <div>
                      <label className="block font-extrabold mb-1">{t("ifsc")} *</label>
                      <input type="text" name="bank_ifsc" value={formData.bank_ifsc} onChange={handleChange} required maxLength={11}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                      <FieldHint>Example: HDFC0ABCDE12</FieldHint>
                    </div>
                    <div>
                      <label className="block font-extrabold mb-1">{t("accType")}</label>
                      <input type="text" name="bank_account_type" value={formData.bank_account_type} onChange={handleChange} placeholder="Saving / Current"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                  </div>

                  {/* Nominee */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block font-extrabold mb-1">{t("nomineeName")}</label>
                      <input type="text" name="nominee_name" value={formData.nominee_name} onChange={handleChange}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div>
                      <label className="block font-extrabold mb-1">{t("nomineeAge")}</label>
                      <input type="number" name="nominee_age" value={formData.nominee_age} onChange={handleChange}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div>
                      <label className="block font-extrabold mb-1">{t("nomineeRel")}</label>
                      <select name="nominee_relation" value={formData.nominee_relation} onChange={handleChange}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <option value="">{t("selectRel")}</option>
                        <option>Father</option>
                        <option>Mother</option>
                        <option>Spouse</option>
                        <option>Brother</option>
                        <option>Sister</option>
                        <option>Son</option>
                        <option>Daughter</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-extrabold mb-1">{t("nomineeAddr")}</label>
                      <input type="text" name="nominee_address" value={formData.nominee_address} onChange={handleChange}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                  </div>

                  {/* Final Gate section (OTP -> Account -> Pay) */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* ✅ OTP UI opens only after Final Submit */}
                    {showFinalOtpBox && (
                      <SoftCard title={t("otpTitle")} icon="🔐" tone={otpVerified ? "ok" : "slate"}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] text-slate-600">{t("otpSub")}</div>
                          {otpVerified ? (
                            <Badge tone="ok">{t("verified")}</Badge>
                          ) : (
                            <Badge tone="info">Step 1/3</Badge>
                          )}
                        </div>

                        <div className="mt-3 flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={sendOtp}
                            disabled={otpLoading || otpVerified || !/^\d{10}$/.test(String(formData.mobile || "").trim())}
                            className={cx(
                              "px-4 py-2 rounded-lg text-[12px] font-extrabold text-white",
                              "bg-slate-900 hover:bg-slate-800 disabled:opacity-60"
                            )}
                          >
                            {otpVerified ? t("verified") : otpSent ? t("resendOtp") : t("sendOtp")}
                          </button>

                          {otpSent && !otpVerified && (
                            <div className="flex gap-2">
                              <input
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                placeholder={t("otpPH")}
                                inputMode="numeric"
                                maxLength={6}
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-[12px] bg-white"
                              />
                              <button
                                type="button"
                                onClick={verifyOtp}
                                disabled={otpLoading}
                                className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-[12px] font-extrabold disabled:opacity-60"
                              >
                                {t("verifyOtp")}
                              </button>
                            </div>
                          )}
                        </div>
                      </SoftCard>
                    )}

                    {/* ✅ Account shows only after OTP verified */}
                    {otpVerified && (
                      <SoftCard title={t("accountTitle")} icon="👤" tone={accountCreated ? "ok" : "info"}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] text-slate-600">{t("accountNote")}</div>
                          {accountCreated ? (
                            <Badge tone="ok">{t("created")}</Badge>
                          ) : (
                            <Badge tone="info">Step 2/3</Badge>
                          )}
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-2">
                          <input
                            type="password"
                            value={accPassword}
                            onChange={(e) => setAccPassword(e.target.value)}
                            placeholder={t("password")}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                          <input
                            type="password"
                            value={accConfirm}
                            onChange={(e) => setAccConfirm(e.target.value)}
                            placeholder={t("confirmPassword")}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                          <button
                            type="button"
                            onClick={createLoginAccount}
                            disabled={accountCreating || accountCreated}
                            className={cx(
                              "px-4 py-2 rounded-lg text-white font-extrabold text-[12px]",
                              accountCreated ? "bg-emerald-700" : "bg-indigo-600 hover:bg-indigo-700",
                              accountCreating ? "opacity-60 cursor-not-allowed" : ""
                            )}
                          >
                            {accountCreated ? t("created") : accountCreating ? "Creating..." : t("create")}
                          </button>
                        </div>

                        {!accountCreated && (
                          <div className="mt-2 text-[11px] text-slate-500">
                            After account creation, click Final Submit again to open payment.
                          </div>
                        )}
                      </SoftCard>
                    )}
                  </div>

                  <div className="mt-4">
                    <SoftCard title={t("docsLaterTitle")} icon="📄" tone="warn">
                      <div className="text-[12px] text-amber-900 leading-relaxed">{t("docsLaterText")}</div>
                    </SoftCard>
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setActiveStep(2)}
                      className="px-6 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-[13px] font-extrabold hover:bg-slate-50"
                    >
                      ← {t("back")}
                    </button>

                    <button
                      type="submit"
                      disabled={submitting || paying}
                      className={cx(
                        "inline-flex items-center justify-center gap-2 text-white font-extrabold text-[13px] md:text-[14px] px-9 md:px-11 py-3 rounded-full shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed",
                        !otpSent
                          ? "bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600"
                          : otpSent && !otpVerified
                          ? "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                          : otpVerified && !accountCreated
                          ? "bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-700 hover:to-sky-700"
                          : "bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 hover:from-purple-700 hover:via-pink-700 hover:to-rose-700"
                      )}
                    >
                      {finalCtaLabel}
                    </button>
                  </div>

                  <div className="mt-3 text-[11px] text-slate-600 text-center">
                    {otpVerified && accountCreated ? (
                      <span className="text-emerald-700 font-extrabold">
                        ✅ Step 3/3 Ready — Payment will open now
                      </span>
                    ) : (
                      <span>
                        🔒 Sequence: <b>Final Submit</b> → <b>OTP</b> → <b>Verify</b> → <b>Create Account</b> → <b>Pay</b>
                      </span>
                    )}
                  </div>

                  {payError && (
                    <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
                      {payError}
                    </div>
                  )}

                  <p className="text-[11px] text-slate-500 text-center mt-3">{t("note")}</p>
                </SectionCard>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
